/**
 * Wrapper de seção de domínio — dá âncora estável (item.id), título com badge
 * de estado de gate e aplica a regra «gate visível, nunca escondido»:
 *   - cap presente ⇒ children (o form real, contrato de salvamento intocado);
 *   - cap ausente + omitWhenBarred ⇒ null (fail-closed, ex.: fila pós-transferência —
 *     reproduz o comportamento hoje em `ConfigTenantPage.tsx:86`, que filtra a aba
 *     `queue-limbo` inteira quando falta `ai-queue-receptionist`; P19/nav-model.ts:9-17);
 *   - cap ausente + tierLabel ⇒ corpo locked (badge de tier + explicação) — NUNCA
 *     escondido, mesmo sem tierLabel (cai no texto genérico "plano superior");
 *   - soon ⇒ corpo tracejado «em breve», sem children.
 *
 * Ordem deliberada: `omitWhenBarred` é checado ANTES de `soon`. Um item nunca tem as
 * duas flags juntas no catálogo hoje, mas se tivesse, fail-closed vence — omitir é
 * mais conservador que anunciar "em breve" algo que o papel não pode nem ver.
 *
 * Fix round 3 (Task 7 Step 0) — o corpo locked usava um `<p>` escrito à mão; o app
 * já tem um primitivo canônico do DS para exatamente isto (`CapabilityLockedBanner`,
 * 34 usos, "Pattern DS v2.2.0 — primitivo universal D-B"), com `role="status"`,
 * `aria-label` e `data-capability`. Era um QUARTO caminho para "gate visível" —
 * as 5 páginas de domínio (T7-T11) compõem `DomainSection`, logo cada uma herdava
 * o desvio. Usa `fallbackMessage` (não `tierMin`): o `tierMin` do DS usa o
 * vocabulário `Professional`/`Regulated`, mas o catálogo (via `nav-model.ts`) usa
 * `Pro`/`Team+` — o mesmo texto que o menu já mostra. Passar `tierMin` trocaria
 * "Pro" por "Professional" e divergiria do nav. A divergência de vocabulário entre
 * os dois primitivos é PRÉ-EXISTENTE e vira item para o TL, não se resolve aqui.
 */
import { CapabilityLockedBanner } from '@lume/ui-components';

import { useCapability } from '../../../hooks/use-capability';

import type { ConfigCatalogItem } from './catalog';

export function DomainSection({
  item,
  children,
}: {
  readonly item: ConfigCatalogItem;
  readonly children: React.ReactNode;
}): JSX.Element | null {
  const ok = useCapability(item.capability);
  if (!ok && item.omitWhenBarred === true) return null;
  return (
    <section
      id={item.id}
      aria-labelledby={`${item.id}-titulo`}
      className="scroll-mt-24 border-b border-border-subtle py-8 last:border-b-0"
    >
      <header className="mb-4 flex items-center gap-2">
        <h2 id={`${item.id}-titulo`} className="text-lg font-semibold text-text-primary">
          {item.label}
        </h2>
        {item.soon === true && (
          <span className="rounded border border-dashed border-border-subtle px-2 py-0.5 text-xs text-text-secondary">
            Em breve
          </span>
        )}
        {!ok && item.tierLabel !== undefined && (
          <span className="rounded bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
            {item.tierLabel}
          </span>
        )}
      </header>
      <p className="mb-4 text-sm text-text-secondary">{item.efeito}</p>
      {item.soon === true ? (
        <p className="rounded-xl border border-dashed border-border-subtle p-4 text-sm text-text-secondary">
          Em breve — este item ainda está em preparação.
        </p>
      ) : !ok ? (
        <CapabilityLockedBanner
          capability={item.capability}
          variant="block"
          fallbackMessage={`Disponível no plano ${
            item.tierLabel ?? 'superior'
          }. Peça ao administrador para liberar (Configurações → Pacote).`}
        />
      ) : (
        children
      )}
    </section>
  );
}
